import { useState, useEffect } from 'react'

export interface DymoPrinter {
  readonly name: string
  readonly modelName: string
  readonly isConnected: boolean
  readonly isLocal: boolean
}

interface DymoFramework {
  getPrinters(): unknown[]
  openLabelXml(xml: string): { print(printerName: string): void }
}

declare global {
  interface Window {
    dymo?: {
      label?: {
        framework?: DymoFramework
      }
    }
  }
}

// Module-level singleton: resolved at most once per page load regardless of
// how many components call useDymoSdk().
type SdkState = { available: boolean; error: string | null; framework: DymoFramework | null }

let singleton: SdkState | null = null
let listeners: Array<(state: SdkState) => void> = []

function notifyListeners(state: SdkState) {
  singleton = state
  listeners.forEach((fn) => fn(state))
}

let pollStarted = false

function startPoll() {
  if (pollStarted) return
  pollStarted = true

  const INTERVAL_MS = 100
  const TIMEOUT_MS = 3000
  const maxTicks = TIMEOUT_MS / INTERVAL_MS
  let ticks = 0

  const id = setInterval(() => {
    ticks++
    const fw = window.dymo?.label?.framework
    if (fw) {
      clearInterval(id)
      notifyListeners({ available: true, error: null, framework: fw })
      return
    }
    if (ticks >= maxTicks) {
      clearInterval(id)
      notifyListeners({
        available: false,
        error:
          'DYMO Connect is not running on this machine; install it from https://dymo.com/setup and refresh the page.',
        framework: null,
      })
    }
  }, INTERVAL_MS)
}

function normalizePrinters(fw: DymoFramework): DymoPrinter[] {
  try {
    const raw = fw.getPrinters()
    return (raw as Array<Record<string, unknown>>).map((p) => ({
      name: String(p['name'] ?? ''),
      modelName: String(p['modelName'] ?? ''),
      isConnected: Boolean(p['isConnected']),
      isLocal: Boolean(p['isLocal']),
    }))
  } catch {
    return []
  }
}

interface UseDymoSdkResult {
  readonly available: boolean
  readonly error: string | null
  readonly printers: readonly DymoPrinter[]
  readonly print: (xml: string, printerName: string) => Promise<void>
  readonly refreshPrinters: () => Promise<void>
}

export function useDymoSdk(): UseDymoSdkResult {
  // Use a lazy initializer so singleton state is read once at mount, not in an
  // effect body (avoids the react-hooks/set-state-in-effect lint rule).
  const [state, setState] = useState<SdkState>(() => singleton ?? { available: false, error: null, framework: null })
  const [printers, setPrinters] = useState<readonly DymoPrinter[]>(() =>
    singleton?.framework ? normalizePrinters(singleton.framework) : [],
  )

  useEffect(() => {
    // If singleton resolved before this component mounted, nothing to subscribe.
    if (singleton) return

    const listener = (s: SdkState) => {
      setState(s)
      if (s.framework) setPrinters(normalizePrinters(s.framework))
    }
    listeners.push(listener)
    startPoll()

    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }, [])

  async function print(xml: string, printerName: string): Promise<void> {
    const fw = singleton?.framework
    if (!fw) throw new Error('DYMO Connect SDK is not available')
    try {
      const lbl = fw.openLabelXml(xml)
      lbl.print(printerName)
    } catch (err) {
      // Re-throw the original error so the SDK error message reaches the caller.
      // Wrapping it would lose the cause chain; just throw whatever the SDK gave us.
      throw err instanceof Error ? err : new Error(String(err))
    }
  }

  async function refreshPrinters(): Promise<void> {
    const fw = singleton?.framework
    if (!fw) return
    setPrinters(normalizePrinters(fw))
  }

  return { available: state.available, error: state.error, printers, print, refreshPrinters }
}
